import { inject, Injectable } from '@angular/core';
import { AuthProvider } from './auth-provider.interface';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthRequiredPermissions } from './auth-required-permissions';
import { OKTA_OAS_AUTH } from './auth-factory.service';
import { IDToken } from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class OasAuthProvider implements AuthProvider {
  private readonly oktaAuth = inject(OKTA_OAS_AUTH);
  private readonly localStorageService = inject(LocalStorageService);

  async signIn(): Promise<boolean> {
    const requiredPermissions = this.localStorageService.get(
      'auth-required-permissions',
    ) as AuthRequiredPermissions;

    // filter out non-oas scopes
    const oasRequiredPermissions = requiredPermissions.oasRequiredPermissions?.filter((scope) => {
      return scope.startsWith('oas:crew-management-api');
    });
    if (!oasRequiredPermissions || !oasRequiredPermissions.length) {
      return Promise.resolve(true);
    }

    const isAuthenticated = await this.isAuthenticated();
    if (isAuthenticated) {
      return Promise.resolve(true);
    }

    await this.oktaAuth.signInWithRedirect({
      scopes: ['openid', 'profile', 'email', 'offline_access', ...oasRequiredPermissions],
      state: 'oasRedirect',
    });
    return Promise.resolve(false);
  }

  isAuthenticated(): Promise<boolean> {
    return this.oktaAuth.isAuthenticated();
  }

  getAccessToken(): Promise<string | null> {
    return this.oktaAuth.getOrRenewAccessToken();
  }

  async parseFromUrl(): Promise<void> {
    const tokenContainer = await this.oktaAuth.token.parseFromUrl();
    if (!tokenContainer?.tokens) {
      return;
    }
    this.oktaAuth.tokenManager.setTokens(tokenContainer.tokens);
  }

  logout(): Promise<boolean> {
    return this.oktaAuth.signOut({ clearTokensBeforeRedirect: true });
  }

  clear(): void {
    this.oktaAuth.tokenManager.clear();
  }

  async getScopes(): Promise<string[]> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.accessToken?.scopes ?? [];
  }

  async getIdToken(): Promise<IDToken | undefined> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.idToken;
  }
}
